import React, { useEffect, useRef } from "react";
import { Box, Textarea } from "@chakra-ui/react";
import { useDraggable } from "../useDraggable";
import { stickyTint } from "../boardTools";
import { STANDE } from "../../standeTokens";

/** A sticky note. Placed by clicking, focused straight away so a teacher can
 *  type without a second click, and dragged by its body. */
const StickyNote = ({ item, isSelected, readOnly, onChange, onMove, onSelect }) => {
  const ref = useRef(null);
  const { onPointerDown } = useDraggable({
    x: item.x, y: item.y, onMove, onStart: onSelect, disabled: readOnly,
  });

  useEffect(() => {
    if (!item.autoFocus || !ref.current) return undefined;
    // The click that created this item has not finished: the browser sets
    // focus itself on mousedown, after our handler, and would blur us again.
    // A macrotask lands after it. Deliberately not requestAnimationFrame,
    // which never fires while the tab is in the background — the caret would
    // silently not arrive.
    const t = setTimeout(() => ref.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [item.autoFocus]);

  return (
    <Box
      position="absolute"
      left={`${item.x}px`}
      top={`${item.y}px`}
      // Its own size once resized, the classic square until then.
      width={`${item.width || 168}px`}
      height={`${item.height || 168}px`}
      display="flex"
      flexDirection="column"
      p="14px"
      borderRadius="4px"
      data-sticky-note
      bg={stickyTint(item.colour)}
      // Lifted a little when chosen; the frame around it says which one.
      boxShadow={
        isSelected ? "0 10px 22px -10px rgba(0,0,0,0.38)" : "0 6px 16px -10px rgba(0,0,0,0.35)"
      }
      cursor={readOnly ? "default" : "grab"}
      _active={{ cursor: readOnly ? "default" : "grabbing" }}
      onPointerDown={onPointerDown}
      transition="box-shadow 0.15s"
    >
      <Textarea
        ref={ref}
        data-no-drag
        value={item.text || ""}
        onChange={(e) => onChange({ text: e.target.value })}
        onFocus={onSelect}
        isReadOnly={readOnly}
        placeholder="Type a note"
        variant="unstyled"
        resize="none"
        flex="1"
        minH={0}
        height="100%"
        fontSize="14px"
        lineHeight="1.45"
        color={STANDE.black}
        _placeholder={{ color: "rgba(34,34,34,0.38)" }}
      />
    </Box>
  );
};

export default StickyNote;
